







































































































import HasValidation from "@/mixins/HasValidation";
import PaymentProvider from "@/components/app/global/payments/paymentProvider.vue";
import { Component, Prop, Mixins } from "vue-property-decorator";
import { DataModules } from "@/store/modules/data";
import { GatewayContext, GatewayStoreType } from "@/data/enums/gateway";
import { ICountry } from "@/models/constants";
import { IInvoice } from "@/models/invoices";
import { IShare } from "@/models/share";
import { QUERY_PARAMS } from "@/data/constants";

interface ISharedInvoiceForm {
  address: {
    address_1: string;
    address_2: string;
    city: string;
    postcode: string;
    country_id: ICountry["id"];
  };
  client: {
    firstname: string;
    lastname: string;
  };
}

@Component({
  components: {
    "select-payment-method": () =>
      import("@/components/app/global/payments/selectPaymentMethodComp.vue"),
    "address-search": () =>
      import("@/components/app/global/address/addressSearch.vue"),
    "address-form": () =>
      import("@/components/app/global/address/addressForm.vue")
  }
})
export default class SharedInvoicePaymentModal extends Mixins(
  PaymentProvider,
  HasValidation
) {
  // @ts-ignore
  public $refs!: {
    firstname: HTMLFormElement;
  };

  @Prop({ required: true })
  public invoice!: IInvoice;

  @Prop({ required: true })
  public share!: IShare;

  public stepTracker = {
    stepNum: 0,
    total: 2
  };

  public isProcessing = false;
  public excludedGatewayStoreTypes = [GatewayStoreType.ALWAYS];

  public form: ISharedInvoiceForm = {
    address: {
      address_1: "",
      address_2: "",
      city: "",
      postcode: "",
      country_id: ""
    },
    client: {
      firstname: "",
      lastname: ""
    }
  };

  // Computed

  public get selectPaymentMethodBindings() {
    return {
      brandId: this.invoice.brand_id,
      currencyCode: this.invoice.currency?.code,
      currencyId: this.invoice?.payment_currency_id || this.invoice.currency_id,
      excludedGatewayStoreTypes: this.excludedGatewayStoreTypes,
      externalAuthReturnLocation: this.externalAuthReturnLocation,
      gatewayContext: GatewayContext.PAY,
      invoiceId: this.invoice?.id,
      isDisabled: this.isProcessing,
      onlineGatewaysOnly: true,
      showAddressField: false,
      totalAmount: this.invoice.unpaid_amount_converted
    };
  }

  public get isCurrentStepValid() {
    return this.currentStep.isValid();
  }

  public get currentStep() {
    return this.steps[this.stepTracker.stepNum];
  }

  public get steps() {
    return [
      {
        label: this.$t("_.select_payment_method"),
        isValid: () =>
          !this.$_.isEmpty(this.paymentMethod) &&
          !this.hasError("card_cvv") &&
          !this.hasError("card_expire_date") &&
          !this.hasError("card_num"),
        cta: () => {
          this.validate().then(valid => {
            if (valid) {
              this.stepTracker.stepNum = 1;
              this.$nextTick(() => {
                this.$refs.firstname && this.$refs.firstname.focus();
              });
            }
          });
        }
      },
      {
        label: this.$t("_.billing_details"),
        isValid: () => {
          if (
            this.requiresAddress &&
            (this.hasError("address_1") ||
              this.hasError("city") ||
              this.hasError("country_id") ||
              this.hasError("postcode"))
          )
            return false;

          return !this.hasError("firstname") && !this.hasError("lastname");
        },
        cta: () => {
          this.validate().then(valid => {
            if (valid) {
              this.validateBeforeSubmit();
            }
          });
        }
      }
    ];
  }

  public get lastStep() {
    return this.stepTracker.stepNum + 1 === this.steps.length;
  }

  //Methods
  public stepTo(nextStepNum) {
    if (nextStepNum > -1 && nextStepNum < this.steps.length) {
      if (nextStepNum < this.stepTracker.stepNum) {
        // Can go back
        this.stepTracker.stepNum = nextStepNum;
      } else {
        // Validate when going forward
        this.validate().then(valid => {
          if (valid) {
            this.stepTracker.stepNum = nextStepNum;
          }
        });
      }
    }
  }

  public onAddressSearchResult(result) {
    this.$_.merge(this.form.address, result);
  }

  public async validateBeforeSubmit() {
    try {
      if (!(await this.validate()))
        throw this.$t("_sentence.form_is_not_valid");

      await this.pay();

      if (this.isExternal) return;
      this.$buefy.toast.open({
        message: this.$t("_.payment_successful") as string
      });
      this.$emit("success");
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    }
  }

  public get requiresAddress() {
    return this.$_.get(this.paymentMethod, "data.requires_address");
  }

  public async pay() {
    this.isProcessing = true;

    try {
      await this.preparePaymentData(this.invoice.client_id);

      const url = `${window.location.origin}${window.location.pathname}?${QUERY_PARAMS.PAYMENT_METHOD_TYPE}=${this.paymentMethodType}&${QUERY_PARAMS.PAYMENT_SUCCESS}=`;
      const urlEncoded = encodeURIComponent(url);

      const externalUrlData = {
        return_url: `?${QUERY_PARAMS.SUCCESS}=${urlEncoded}true&${QUERY_PARAMS.FAILED}=${urlEncoded}false`,
        cancel_url: `${url}cancel`
      };

      const data = {
        params: {
          ...this.$_.pick(this.form, "client"),
          ...(this.requiresAddress ? this.$_.pick(this.form, "address") : {}),
          ...this.$_.omit(this.paymentData, ["address_id", "amount"]),
          ...externalUrlData,
          ...{
            currency_id:
              this.invoice?.payment_currency_id || this.invoice?.currency_id
          }
        }
      };

      const { approval_url } = await this.$store.dispatch(
        `data/${DataModules.SHARES}/postToAction`,
        {
          token: this.share.token,
          action: `payment.store`,
          data
        }
      );

      // If approval (SCA) is required, redirect user off-site
      if (approval_url?.url)
        await this.externalAuthHandler.redirectToExternalUrl(approval_url);
    } finally {
      this.isProcessing = false;
    }
  }
}
